import React, { useState } from 'react'
import { Loader2, Zap, CreditCard, ExternalLink } from 'lucide-react'
import Dialog from '../ui/dialog'
import Button from '../ui/button'
import { instantTopUpEpisode, createTopUpCheckoutSession } from '../../api/billing'

export interface EpisodeTopUpDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Whether the user has a saved card we can instantly charge. */
  hasSavedPaymentMethod: boolean
  /** Top-up price in USD. */
  priceUsd: number
  /** Called after a successful instant charge so caller can retry the gated action. */
  onTopUpSucceeded: () => void
}

const EpisodeTopUpDialog: React.FC<EpisodeTopUpDialogProps> = ({
  open,
  onOpenChange,
  hasSavedPaymentMethod,
  priceUsd,
  onTopUpSucceeded,
}) => {
  const [isCharging, setIsCharging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleInstantCharge = async () => {
    setIsCharging(true)
    setError(null)
    try {
      await instantTopUpEpisode()
      onOpenChange(false)
      onTopUpSucceeded()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed.'
      setError(message)
    } finally {
      setIsCharging(false)
    }
  }

  const handleCheckoutFallback = async () => {
    setIsCharging(true)
    setError(null)
    try {
      const { url } = await createTopUpCheckoutSession({ kind: 'episode' })
      window.location.href = url
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start checkout.'
      setError(message)
      setIsCharging(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="sm" heightMode="auto" className="max-w-md">
      <div className="flex flex-col items-center text-center space-y-5">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50">
          <Zap className="h-6 w-6 text-brand-600" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">You're out of episodes</h3>
          <p className="text-sm text-foreground/60">
            Add <span className="font-semibold text-foreground">+1 episode</span> to your account for{' '}
            <span className="font-semibold text-foreground">${priceUsd.toFixed(2)}</span> and generate this episode right now.
          </p>
        </div>

        {error && (
          <div className="w-full rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-left text-xs text-danger-700">
            {error}
          </div>
        )}

        <div className="flex w-full flex-col gap-2">
          {hasSavedPaymentMethod ? (
            <>
              <Button
                onClick={handleInstantCharge}
                disabled={isCharging}
                className="flex w-full cursor-pointer items-center justify-center gap-2"
              >
                {isCharging ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Processing…
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" /> Charge saved card · ${priceUsd.toFixed(2)}
                  </>
                )}
              </Button>
              <button
                onClick={() => onOpenChange(false)}
                disabled={isCharging}
                className="cursor-pointer text-xs text-foreground/50 hover:text-foreground"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCheckoutFallback}
                disabled={isCharging}
                className="flex w-full cursor-pointer items-center justify-center gap-2"
              >
                {isCharging ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Redirecting…
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4" /> Buy +1 episode · ${priceUsd.toFixed(2)}
                  </>
                )}
              </Button>
              <button
                onClick={() => onOpenChange(false)}
                disabled={isCharging}
                className="cursor-pointer text-xs text-foreground/50 hover:text-foreground"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default EpisodeTopUpDialog
